/**
 * PluginsService
 *
 * Fetches a plugin's resolved member skills from the BFF (`GET /api/plugins/members?ids=...`).
 * The BFF hop is deliberate: resolving N member ids means N experience-API calls, and the listings
 * service only allows CORS from allowlisted deployment origins, so the server makes the calls. See
 * `packages/appx-site-server/src/api/plugins/plugins.service.ts`.
 *
 * Mirrors the try/catch -> handleException -> [] fallback pattern used by skillsService.
 */
import { APPX_SITE_API_URL } from '~/modules/config';
import { handleException } from '~/modules/utilities/error_utilities';
import { http } from '~/modules/utilities/http';

/**
 * A plugin member's card-ready fields. `id` is the member's `external_id` (e.g.
 * 'skill-agentforce-generate') — the caller builds the listing link itself (the same
 * `Routes.LISTING` + `listingId=` pattern used by `moreFromThisProviderV2`/`routingHelper`), since
 * the client already owns that domain-aware (4-experience) URL logic.
 */
export interface PluginMember {
    id: string;
    name: string;
    description: string;
    publisher: string;
    logoUrl: string;
    technology: string;
    domains?: string[];
}

async function getMembers(ids: string[], errorContext: string): Promise<PluginMember[]> {
    try {
        const { data } = await http.get(
            `${APPX_SITE_API_URL}/plugins/members?ids=${ids.map(encodeURIComponent).join(',')}`,
            {
                // This service owns the anticipated-degradation classification below. Suppress the
                // shared HTTP layer's default error log so one failure produces one Splunk record.
                suppressErrorLogging: true,
            },
        );
        return data ?? [];
    } catch (error) {
        // `warn`, not the default `error`: per docs/logging-decision-guide.md anti-pattern
        // "`logger.error` for an anticipated timeout or 4xx on a non-critical endpoint —
        // over-classifies expected degradation — use `logger.warn`". Every caller (the plugin's
        // member list / Specs tab) already degrades to an empty/hidden section on [], so a failure
        // here is anticipated, not page-breaking.
        handleException(error, errorContext, { level: 'warn' });
        return [];
    }
}

export function getPluginMembers(ids: string[]): Promise<PluginMember[]> {
    return getMembers(ids, 'pluginsService.getPluginMembers');
}

/**
 * Resolves a skill listing's `relatedSkills` ids through the same bounded, cached BFF lookup used
 * for plugin members. The route remains shared until OAF provides a bulk-by-id experience API.
 */
export function getRelatedSkills(ids: string[]): Promise<PluginMember[]> {
    return getMembers(ids, 'pluginsService.getRelatedSkills');
}
